import {
  calculateMillisOnPage,
  calculateMillisOnSite,
  calculateSecondsOnPage,
  getSessionFacts
} from "../Session/sessionFacts";

import {castArray, every, isEmpty, keys, pick, some} from "lodash";
import {getTaggedLogger} from "../Session/taggedLogging";
import {readKlaviyoFormHistory} from "../Klaviyo/klaviyoFormStatus";
import {getParams} from "../lib/params";

const logger = getTaggedLogger(null) //'PageTriggers');

/**
 * Called by PageTriggerInvoker, this will look at the PageTrigger rules and the user's
 * SessionFacts and Klaviyo status.
 *
 * @param triggers
 * @param now
 * @param sessionFacts
 * @returns [ triggerResult ]
 */
export function evaluatePageTriggers({triggers, now, sessionFacts}) {
  if (isEmpty(triggers) || !Array.isArray(triggers)) {
    return generateErrorResult({message: "triggers is empty or not an array!"});
  }

  now ||= new Date();
  sessionFacts ||= getSessionFacts();

  return triggers.map(trigger =>
    evaluatePageTrigger({trigger, now, sessionFacts}));
}

/**
 * Evaluate an individual Page Trigger and all of its conditions.
 *
 * @param trigger
 * @param now
 * @param sessionFacts
 * @returns {{triggered: boolean, triggerId}}
 */
function evaluatePageTrigger({trigger, now, sessionFacts}) {
  now ||= new Date();
  sessionFacts ||= getSessionFacts();

  // TODO:
  //  get pageTriggerStatus from cachedLocalStorage
  //  look at triggeredAt for this trigger
  //  triggerHistory: [ timestamp, timestamp, ...]
  //  use maxOccurances and resetTime
  // return without evaluating if these forbid another popup
  let result = {
    triggered: false,
    trigger
  }

  // top level conditions only.
  let conditions = (trigger.conditions || []).filter(c => !c.parentId);

  // trigger:
  // id, operator, name,flavour, desktopAction, mobileAction, resetSeconds, conditions, properties,
  // intervalSeconds, maxOccurrences
  try {
    let results = conditions.map(condition =>
      evaluateTriggerCondition({condition, trigger, now, sessionFacts})
    );

    result.testResults = results;

    let triggered = (trigger.operator === 'or') ?
      some(results, r => r.success) : every(results, r => r.success);

    if (triggered) {
      result.triggered = triggered;
    }
  } catch (err) {
    logger.error(`evaluatePageTriggers: ${err}`, err);
    result.error = err;
  }

  return result;
}

//
function evaluateTriggerCondition({condition, trigger, now, sessionFacts}) {
  const {
    flavour,
    negating,
    valueList: targetValues,
    operator = 'operator_is_undefined'
  } = condition;

  // Get the values present in the page, to compare to targetValues
  const actualValues = getActualValues({condition, now, sessionFacts});

  // now compare actualValues with targetValues
  let result = applyOperator({operator, actualValues, targetValues}) || {};
  //console.log(`evaluate ${flavour} ${condition.paramName} actual=${JSON.stringify(actualValues)} op=${operator}
  // ${targetValues}: ${JSON.stringify(result)}`)

  // this extra stuff is solely for debugging output!
  result = Object.assign({success: null}, result,
    {condition, actualValues, targetValues})

  if (result.error) {
    result.success = false;
  } else if (negating) {
    result.success = !result.matched;
  } else {
    result.success = result.matched;
  }

  return result;
}

const OPERATORS = {
  equals: (actual, target) => { return ("" + actual) === ("" + target) },         // force to string
  starts_with: (actual, target) => { return ("" + actual).startsWith("" + target) }, // force to string
  present: (actual, _target) => { return !isEmpty(actual) },

  greater_or_equal: (actual, target) => { return (+actual) >= (+target); },   // strictly numeric
  less_or_equal: (actual, target) => { return (+actual) <= (+target); },   // strictly numeric
  matches: (actual, regex) => { return !!("" + actual).match(new RegExp(regex)); },
  debug: (actual, target, operator) => {
    logger.error(`UNDEFINED ${operator} on ${actual} vs ${target}`);
    return false
  }
}

function applyOperator({operator, actualValues = [], targetValues = []}) {
  let func = OPERATORS[operator];

  if (!func) {
    return generateErrorResult({message: `no such operator: ${operator}`})
  }

  // now iterate through both lists
  for (let actual of actualValues) {
    for (let target of targetValues) {
      //console.log(`COMPARE: -- ${actual} ${operator} ${target}`)
      let matched = func(actual, target, operator);
      if (matched) {
        return {matched: true, actual, target}
      }
    }
  }

  return {success: false};
}

function getActualValues({condition, now, sessionFacts, klaviyoStatus}) {
  const {flavour, paramName} = condition;

  if (flavour === 'param') {
    let p = sessionFacts.currentParams || getParams();
    return (p && paramName) ? castArray(p[paramName]) : [];
  } else if (flavour === 'first_page_param') {
    let p = sessionFacts.firstPageParams;
    return (p && paramName) ? castArray(p[paramName]) : [];
  } else if (flavour === 'url_path') {
    let path = '' + (globalThis.location?.pathname);
    return [path];
  } else if (flavour === 'first_page_referrer') {
    return ['' + (sessionFacts.firstPageReferrer)];
  } else if (flavour === 'seconds_on_page') {
    let ms = calculateMillisOnPage({sessionFacts, now});
    return [Math.round(ms / 1000)];
  } else if (flavour === 'seconds_on_site') {
    let ms = calculateMillisOnSite({sessionFacts, now});
    return [Math.round(ms / 1000)];
  } else if (flavour === 'page_count') {
    return [sessionFacts.pageCount];
  } else if (flavour === 'group_tag') {
    // always null on non-group pages, no need to warn.
    const {productGroup} = globalThis;
    return isEmpty(productGroup) ? [] : castArray(productGroup.tags || []);
  } else if (flavour === 'category') {
    // always null on non-group pages, no need to warn.
    const {productGroup} = globalThis;
    // return category NAME
    return isEmpty(productGroup) ? [] : castArray(productGroup.topCategory || []);
  } else if (flavour === 'seen_klaviyo_form') {
    klaviyoStatus ||= readKlaviyoFormHistory();
    return keys(klaviyoStatus);
  } else if (flavour === 'completed_klaviyo_form') {
    klaviyoStatus ||= readKlaviyoFormHistory();
    return keys(klaviyoStatus).filter(k => klaviyoStatus[k].submitted);
  }

  logger.warn(`Unknown pageTriggerCondition flavour: '${flavour}'`);
  return [];
}

function generateErrorResult(result = {}) {
  let constants = {error: true, success: false};

  // constants is in here the first tiem for key order, second time as overrides
  result = Object.assign({}, constants, result, constants);

  let {message = "Unknown error"} = result;
  logger.error(message);

  return result;
}
